import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Briefcase } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a contractor invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A contractor invoice should include: your name or company name and address; your client name and address; a unique invoice number; the invoice date and payment due date; a description of the work or services provided; hours worked or project fee; subtotal, any applicable tax (VAT, GST, or sales tax), and the total amount due; your payment details (bank account, wire transfer instructions, or payment link).',
      },
    },
    {
      '@type': 'Question',
      name: 'How do independent contractors bill clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Independent contractors bill clients by creating an invoice that lists the services provided, the agreed rate (hourly or project-based), and the total amount due. Send the invoice via email at the agreed billing frequency, such as on project completion, weekly, or monthly. Include clear payment terms and your bank details so the client can pay promptly.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should a contractor invoice include tax?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Whether you include tax depends on your registration status and country. In the UK, you add VAT only if VAT-registered (turnover over £90,000). In Australia, add 10% GST if your turnover exceeds AUD $75,000. In the US, most service contractors do not charge sales tax, though rules vary by state. In India, add GST if your turnover exceeds ₹20 lakh.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use an invoice template for IT contractors?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The Invilic contractor invoice template works for IT contractors, software developers, web developers, and any other technical consultant. Add your services as line items, include your day rate or hourly rate and number of days/hours, and download a professional PDF.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this contractor invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free contractor invoice template. Fill in online, download as a professional PDF. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function ContractorInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Contractor Invoice Template: Free PDF for Independent Contractors | Invilic"
        description="Free contractor invoice template for independent contractors, IT contractors, and consultants. Bill by hour or project. Download professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/contractor-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Contractor Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for independent contractors, IT contractors, and consultants. Bill by the hour, day rate, or fixed project fee. Fill in online and download a PDF instantly for free.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-slate-800/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Who uses this */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Contractor
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'IT & software contractors', desc: 'Bill clients for development sprints, consulting days, or fixed-price delivery projects.' },
              { title: 'Construction contractors', desc: 'Invoice for labour, materials, and project milestones with a clear payment schedule.' },
              { title: 'Marketing contractors', desc: 'Bill for campaigns, content creation, SEO, or ad management on a retainer or project basis.' },
              { title: 'Engineering contractors', desc: 'Issue professional invoices for technical work, site visits, and project deliverables.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800/60 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Contractor Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name or company name & logo',
              'Client / company name and address',
              'Invoice number and date',
              'Payment due date',
              'Work description per line item',
              'Quantity (hours or days) and rate',
              'Line item totals calculated automatically',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
              'PO number (optional)',
              'Payment terms and notes',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-slate-100 dark:border-slate-700/40 bg-slate-50 dark:bg-slate-900/30">
                <CheckCircle className="w-4 h-4 text-slate-600 dark:text-slate-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Use the Contractor Invoice Template
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your work', desc: 'Enter your details, client info, services, and rate. Takes under 2 minutes.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF ready to email to your client or upload to their portal.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-slate-800 dark:bg-slate-700 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Contractor Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Contractor Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-slate-800/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Contractor Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
